import React, { useState } from 'react';

import { Connect } from '@rover/react-lib/src/components/utils/Redux';
import events from '@rover/react-lib/src/pages/search/SearchPage/analytics';
import { selectors as currentUserSelectors } from '@rover/react-lib/src/redux/ducks/currentUser.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import { selectors as petsSelectors } from '@rover/react-lib/src/redux/ducks/pets.duck';
import { selectors as searchSelectors } from '@rover/react-lib/src/redux/ducks/search.duck';
import { selectors as serviceTypeSelectors } from '@rover/react-lib/src/redux/ducks/serviceTypes.duck';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import emitMetric from '@rover/utilities/emitMetric';

import { AdvancedFiltersBottomSheet } from './components/BottomSheets/AdvancedFiltersBottomSheet';
import {
  SearchFiltersAllOpen,
  SearchFiltersClose,
  SearchFiltersDone,
  SearchFiltersPrimaryOpen,
} from './components/Filters/Collections/Wizard/analyticsEvents';
import PrimaryFiltersBottomSheet from './components/StickyFiltersBar/PrimaryFiltersBottomSheet/PrimaryFiltersBottomSheet';
import type { FilterType } from './components/StickyFiltersBar/StickyFiltersBar';
import StickyFiltersBar from './components/StickyFiltersBar/StickyFiltersBar';
import { AdvancedFiltersModal } from './components';
import { selectors as searchFiltersSelectors } from './SearchFilters.duck';
import { effects as searchPageEffects } from './SearchPage.duck';

const selector = (state) => ({
  searchFilters: searchFiltersSelectors.getFilters(state),
  serviceType: (() => {
    const filters = searchFiltersSelectors.getFilters(state);
    const options = searchFiltersSelectors.getPetTypeBrowsableServiceTypeOptions(state);
    const serviceType = options.find((option) => option.value === filters.serviceType);
    return serviceType || null;
  })(),
  locale: frontendConfigurationSelectors.getLanguage(state),
  isUserAuthenticated: currentUserSelectors.isAuthenticated(state),
  pets: petsSelectors.getPets(state),
  allServiceTypeFilterOptions: searchSelectors.getSearchFiltersOptions(state),
  petSizeBuckets: searchSelectors.getSearchDogSizeBuckets(state),
  weightUnit: searchSelectors.getSearchWeightUnit(state),
  getServiceTypeBySlug: serviceTypeSelectors.getServiceTypeBySlug(state),
  advancedFiltersCount: searchFiltersSelectors.getAdvancedFiltersCount(state),
});

const effects = {
  updateFiltersAndFireSearch: searchPageEffects.updateFiltersAndFireSearchOnChange,
  fireSearchPageSearchCTAEvent: searchPageEffects.fireSearchPageSearchCTAEvent,
};

export type Props = {
  onSave: () => void;
};

const StickyFiltersBarContainer = ({ onSave }: Props): JSX.Element => {
  const [isPrimaryFiltersOpen, setIsPrimaryFiltersOpen] = useState(false);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);

  const isFiltersM2 = useFlag(FLAGS.ROLLOUT_MOBILE_FILTERS_M2);

  const handleFilterClick = (filterType: FilterType): void => {
    if (filterType === 'main') {
      setIsPrimaryFiltersOpen(true);
      emitAnalyticsEvent(new SearchFiltersPrimaryOpen());
    } else if (filterType === 'all') {
      setIsAdvancedFiltersOpen(true);
      emitAnalyticsEvent(new SearchFiltersAllOpen());
    }
  };

  const handlePrimaryFiltersClose = (saved): void => {
    if (saved) onSave();
    setIsPrimaryFiltersOpen(false);
  };

  const handleAdvancedFiltersClose = (saved): void => {
    setIsAdvancedFiltersOpen(false);
    if (saved) {
      onSave();
      emitAnalyticsEvent(new SearchFiltersDone({ sheet: 'all-filters' }));
    } else {
      emitMetric('filters-bottom-sheet-close', { sheet: 'all-filters' });
      emitAnalyticsEvent(new SearchFiltersClose({ sheet: 'all-filters' }));
    }
  };

  return (
    <Connect
      selector={selector}
      effects={effects}
      onMount={() =>
        searchPageEffects.fireAnalyticsEvent(events.SearchOwnerPreferencesDisplayEvent)
      }
    >
      {(connectedProps) => (
        <>
          <StickyFiltersBar
            {...connectedProps}
            onFilterClick={handleFilterClick}
            advancedFiltersCount={isFiltersM2 ? connectedProps.advancedFiltersCount : undefined}
          />
          <PrimaryFiltersBottomSheet
            {...connectedProps}
            isOpen={isPrimaryFiltersOpen}
            onClose={handlePrimaryFiltersClose}
          />
          {isFiltersM2 ? (
            <AdvancedFiltersBottomSheet
              isOpen={isAdvancedFiltersOpen}
              onClose={handleAdvancedFiltersClose}
            />
          ) : (
            <AdvancedFiltersModal
              {...connectedProps}
              isOpen={isAdvancedFiltersOpen}
              onClose={handleAdvancedFiltersClose}
            />
          )}
        </>
      )}
    </Connect>
  );
};

export default StickyFiltersBarContainer;
